import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, makeStyles } from '@material-ui/core';
import styles from './Header.module.scss';
import { getRegistrationUrl, getTranslation } from '../../utils/functions';
import { useLocation } from 'react-router-dom';

import {
  defaultLanguageSelector,
  headerSelector,
  languagesSelector,
  primaryColorSelector,
  showMyAccreditationSelector,
  textsOverwritingsSelector,
} from '../../store/selectors/globalConfigSelectors';
import RPLHeader from '@hid-galaxy-ui/galaxy-react/components/Header';
import { isAuthenticated } from '../../utils/authService';
import { userFirstNameSelector } from '../../store/selectors/userSelectors';
import { removeLocalStorageItem } from '../../utils/sessionUtils';
import userActions from '../../store/actions/userActions';
import { useHistory } from 'react-router-dom';
import buildPATHS, { PATHS_WITH_PARAMS } from '../../utils/paths';
import {
  urlParamsSelector,
  languageSelector,
  accountRegisterUrlSelector,
} from '../../store/selectors/appSelectors';
import languagesList from '../../constants/languagesList';
import allActions from '../../store/actions';
import {
  loginEnabledSelector,
  createAccountEnabledSelector,
} from '../../store/selectors/formSessionSelectors';
import { isQueryParamsExist } from '../../utils/functions';
import { getPathWithBaseUrl } from '../../utils/paths';
import formSessionActions from '../../store/actions/formSessionActions';
import { BOLOGNA_LOGO_URL } from '../../constants/common';
import { isBolognaEnv } from '../../utils/common';
import { ACCESS_TOKEN_KEY } from '../../constants/sessionStorage';
import Logo from '../Logo/Logo';

const useParagraphStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.common.white,
    marginRight: 'auto',
  },
}));

const Header = () => {
  const {
    primaryColor,
    languages,
    showMyAccreditation,
    topHeaderText,
    textsOverwritings,
    header,
    firstName,
    urlParams,
    defaultLanguage,
    language,
    accountRegisterUrl,
  } = useSelector((state) => ({
    primaryColor: primaryColorSelector(state),
    languages: languagesSelector(state),
    showMyAccreditation: showMyAccreditationSelector(state),
    topHeaderText: headerSelector(state)?.topHeaderText,
    textsOverwritings: textsOverwritingsSelector(state),
    header: headerSelector(state),
    firstName: userFirstNameSelector(state),
    urlParams: urlParamsSelector(state),
    defaultLanguage: defaultLanguageSelector(state),
    language: languageSelector(state),
    accountRegisterUrl: accountRegisterUrlSelector(state),
  }));

  const getLanguageName = (languageCode) => languagesList[languageCode]?.name;

  const dispatch = useDispatch();

  const paragraphClasses = useParagraphStyles();
  const loginEnabled = useSelector(loginEnabledSelector);
  const createAccountEnabled = useSelector(createAccountEnabledSelector);
  const history = useHistory();
  const PATHS = buildPATHS();

  const [selectedLanguage, setSelectedLanguage] = useState({
    label: getLanguageName(language),
    value: language,
  });

  useEffect(() => {
    if (language) {
      setSelectedLanguage({
        label: getLanguageName(language),
        value: language,
      });
    }
  }, [language]);

  const logo = () =>
    isBolognaEnv() ? (
      <div className="hid-header__logo">
        <img className="hid-header__logo" src={BOLOGNA_LOGO_URL} />
      </div>
    ) : header?.logoUrl ? (
      <div className="hid-header__logo">
        <img className="hid-header__logo" src={header.logoUrl} />
      </div>
    ) : (
      <Logo className="hid-header__logo" />
    );

  const { t, i18n } = useTranslation();
  const themeStyles = {
    background: primaryColor,
  };

  const getProfileActions = () => [
    {
      icon: 'circleUser',
      key: 'user',

      ...(isAuthenticated() && {
        isUserAction: true,
        subNavItems: [
          {
            data: [
              {
                key: 'myAccount',
                value: getTranslation('MY_ACCOUNT'),
              },
              {
                key: 'logout',
                value: getTranslation('LOGOUT'),
              },
            ],
          },
        ],
      }),
    },
  ];

  const getAnonymousActions = () => [
    {
      icon: 'circleUser',
      key: 'login',

      isUserAction: true,
      subNavItems: [
        {
          data: [
            ...(loginEnabled
              ? [
                  {
                    key: 'login',
                    value: getTranslation('LOGIN'),
                  },
                ]
              : []),
            ...(createAccountEnabled
              ? [
                  {
                    key: 'account_register',
                    value: getTranslation('REGISTER'),
                  },
                ]
              : []),
          ],
        },
      ],
    },
  ];

  const handleLogout = () => {
    // clearLocalStorage();
    removeLocalStorageItem(ACCESS_TOKEN_KEY);
    dispatch(userActions.resetAccountData());
    dispatch(formSessionActions.resetFormSession());
    redirectToLogin();
  };

  const handleLogin = () => {
    redirectToLogin();
  };

  const redirectToLogin = () => {
    redirectToUrl(getPathWithBaseUrl(PATHS_WITH_PARAMS.login), PATHS.login);
  };

  const handleRegister = () => {
    redirectToUrl(getRegistrationUrl(), PATHS.account_register);
  };

  const redirectToUrl = (url, queryParamurl = null) => {
    if (isQueryParamsExist() && queryParamurl) {
      history.push(queryParamurl);
    } else {
      history.push(url);
    }
  };

  const handleMyAccount = () => {
    redirectToUrl(getPathWithBaseUrl(PATHS_WITH_PARAMS.my_accounts));

    // if(isQueryParamsExist()) {
    //   history.push(PATHS.my_accounts);
    // } else {
    //   history.push(getPathWithBaseUrl(PATHS_WITH_PARAMS.my_accounts));
    // }
  };

  const getLanguageOptions = () => {
    return languages.map((lng) => {
      return {
        label: getLanguageName(lng),
        value: lng,
      };
    });
  };

  const isMultipleLanguage = languages.length > 1;

  const handleActionClick = (e, key) => {
    dispatch(allActions.appConfigActions.setAppError(null));

    switch (key) {
      case 'login':
        handleLogin();
        break;
      case 'account_register':
        handleRegister();
        break;
      case 'logout':
        handleLogout();
        break;
      case 'myAccount':
        handleMyAccount();
        break;
    }
  };

  const handleLanguageChange = (selectedLng) => {
    setSelectedLanguage(selectedLng);
    i18n.changeLanguage(selectedLng.value);
    dispatch(allActions.appConfigActions.setLanguage(selectedLng.value));

    // dispatch(allActions.globalConfigActions.setGlobalLanguages([selectedLng.value]))
  };
  const getActions = () => {
    if (isAuthenticated()) {
      return getProfileActions();
    }
    if (loginEnabled || createAccountEnabled) {
      return getAnonymousActions();
    }
    return [];
  };

  return (
    // <div className={styles.headerWrapper} style={themeStyles}>
    <RPLHeader
      className={styles.header}
      logo={logo()}
      disableHamburger
      actions={getActions()}
      actionCallback={handleActionClick}
      userName={isAuthenticated() ? firstName : ''}
      title={
        topHeaderText
          ? getTranslation(topHeaderText)
          : getTranslation('HEADER_MSG')
      }
      originHref={urlParams ? PATHS.register : PATHS.login}
      {...(isMultipleLanguage && {
        actionDropDownConfig: {
          key: 'actionDropDown',
          options: getLanguageOptions(),
          placeholder: getLanguageName(selectedLanguage?.value),
          selectedValue: selectedLanguage,
          onChange: (value) => {
            handleLanguageChange(value);
          },
        },
      })}
    />
    // </div>

    // <nav className={styles.nav} style={themeStyles}>
    //   <header className={styles.header}>
    //     <ContentWrapper className={styles.headerContent}>
    //       {topHeaderText && (
    //         <Typography className={paragraphClasses.root}>
    //           {getTranslation(topHeaderText)}
    //         </Typography>
    //       )}

    //       {showMyAccreditation && (
    //         <NavLink className={styles.link} to="#">
    //           {getTranslation(textsOverwritings, 'MY_ACCREDITATIONS') || t('MY_ACCREDITATIONS')}
    //         </NavLink>
    //       )}

    //       {languages.length > 1 && <LanguageSelection />}
    //     </ContentWrapper>
    //   </header>
    // </nav>
  );
};

export default Header;
